import * as React from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import {
  Activity,
  ArrowUpDown,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleDashed,
  Moon,
  Search,
  Sun,
  SunMoon,
  Users,
  X,
} from 'lucide-react'
import {
  CUSTOMER_COLUMN_FILTERS,
  LIVE_STATES,
  LIVE_STATE_META,
  SEGMENTED_AGENT_USERNAMES,
  SEGMENT_AGENT_USERNAME,
  SEGMENT_LABEL,
  SHIFT_FILTERS,
  SHIFT_FILTER_PRIORITY,
  type CustomerColumnFilter,
  type CustomerQuery,
  type CustomerSegment,
  type CustomerStatus,
  type LiveState,
  type ShiftFilter,
} from '@acs/shared'
import { apiGet } from '@/lib/api'
import { cn } from '@/lib/cn'
import { Button } from '@/components/ui/button'
import { Dropdown, type DropdownItem } from '@/components/ui/dropdown'
import { Input } from '@/components/ui/input'
import { Kbd } from '@/components/ui/kbd'
import { Tooltip } from '@/components/ui/tooltip'
import { SHIFT_PRIORITY_LABEL } from '@/components/customers/shift-priority'

/**
 * The customer table's filter bar — and the single source of truth for the
 * table's *view contract*: filters, sort **and pagination**.
 *
 * All of it lives in the URL, not in component state. That is not a purity
 * exercise: an operator who spots something odd has to be able to paste the
 * address bar into chat and have the person on the other end see the same
 * rows — the same page of them. It also means back/forward undo a filter, and
 * a reload does not drop the operator back to "everything, page 1".
 *
 * The search-param helpers below are exported because the table reads the same
 * params. Keeping the parser here — next to the controls that write it — is
 * what stops the reader and the writer from drifting apart.
 */

/* ------------------------------------------------------------------ *
 * Search-param contract
 * ------------------------------------------------------------------ */

export type CustomerSort = NonNullable<CustomerQuery['sort']>

/**
 * Rows per page. Three sizes is the whole menu: 25 for a phone, 50 for a
 * laptop, 100 for someone triaging a backlog on a big screen. Anything larger
 * stops being a page and starts being the full-table fetch this replaced.
 */
export const PAGE_SIZES = [25, 50, 100] as const
export type PageSize = (typeof PAGE_SIZES)[number]
export const DEFAULT_PAGE_SIZE: PageSize = 50

/**
 * The header filter row: one parameter per filterable column.
 *
 * Re-exported from the wire contract rather than declared again here. Six
 * things iterate this list — the URL parser, the URL serialiser, the "is
 * anything filtering?" test, "clear all", the chips, the row of inputs — plus
 * `normalizeQuery`, which builds the request itself. A second copy of the same
 * seven names is how one consumer silently falls behind, and the one that did
 * was the request builder: the URL filtered and the network call did not.
 */
export const COLUMN_FILTERS = CUSTOMER_COLUMN_FILTERS
export type ColumnFilter = CustomerColumnFilter

/** What a chip calls each one. */
export const COLUMN_FILTER_LABELS: Readonly<Record<ColumnFilter, string>> = Object.freeze({
  name: 'Customer',
  agentName: 'Agent',
  email: 'Email',
  job: 'Job',
  schedule: 'Schedule',
  city: 'City',
  workHours: 'Work hours',
  computerNumber: 'Number',
})

export type CustomerSearch = {
  q: string
  agent: string[]
  liveState: LiveState[]
  /** The Shift filter: `day` / `night` / `any` / `unset`, any combination. */
  shift: ShiftFilter[]
  jobId: string
  scheduleId: string
  sort: CustomerSort
  dir: 'asc' | 'desc'
  /*
   * The two OUTCOME filters, and the reason they are booleans rather than a
   * single enum: they are not opposite ends of one axis. "Claimed a shift" and
   * "saw one and missed it" are separate durable facts, a customer can have
   * neither, and the lists are built from two different indexed fields. An enum
   * would also invite a third value meaning "not won", which is an absence no
   * index can serve — the trap the API's own note warns about.
   */
  won: boolean
  activeHit: boolean
  /** 1-based. Page 1 is the default and is omitted from the URL. */
  page: number
  /** Rows per page; always one of `PAGE_SIZES`. */
  size: PageSize
} & Record<ColumnFilter, string>

/** Every column filter at its default — the empty string. */
const EMPTY_COLUMN_FILTERS = Object.freeze(
  Object.fromEntries(COLUMN_FILTERS.map((key) => [key, ''])) as Record<ColumnFilter, string>
)

export const DEFAULT_CUSTOMER_SEARCH: CustomerSearch = Object.freeze({
  q: '',
  agent: [],
  liveState: [],
  shift: [],
  jobId: '',
  scheduleId: '',
  won: false,
  activeHit: false,
  sort: 'created_at',
  dir: 'desc',
  page: 1,
  size: DEFAULT_PAGE_SIZE,
  ...EMPTY_COLUMN_FILTERS,
})

/* ------------------------------------------------------------------ *
 * The sort a list opens on
 * ------------------------------------------------------------------ */

export interface SortDefault {
  sort: CustomerSort
  dir: 'asc' | 'desc'
}

/**
 * Which order each list opens in when the URL does not say.
 *
 * ── Why Confirmed is different ──────────────────────────────────────
 * Pending and Approved are queues an operator works through, and "newest
 * customer first" is the order they were added in — which is the order the work
 * arrived in. Confirmed is not a queue. Nobody adds a customer to it; a customer
 * lands there because a worker claimed their shift, and the claim writes
 * `status: 'confirmed'` and stands the run down in one operation.
 *
 * That is what made this list unreadable. A win removed the row from Sessions
 * and dropped it into a 268-row list ordered by SIGNUP date, so a shift claimed
 * ten seconds ago could surface on page 4 between two customers from March.
 * There was no way to answer "who just won" from the screen at all.
 *
 * `won_at desc` answers it: the claim that just landed is row one. Rows with no
 * `won_at` — customers moved here by hand, who never won anything — sort below
 * every real win, because a Date outranks a missing field in Mongo's descending
 * order. They keep a stable order among themselves; see `sortFor` in the repo.
 *
 * ── Why a default and not a link ────────────────────────────────────
 * The rail could simply carry `?sort=won_at` on its Confirmed link, and that
 * would work for exactly one of the ways an operator reaches this list. The
 * command palette, a bookmark, the redirect after Confirming a selection and a
 * hand-typed `/customers/confirmed` would all still open in signup order — the
 * same list, in two different orders, depending on the door used. A default
 * belongs to the view, not to one link into it.
 */
const DEFAULT_SORT_BY_STATUS: Readonly<Record<CustomerStatus, SortDefault>> = Object.freeze({
  pending: { sort: 'created_at', dir: 'desc' },
  approved: { sort: 'created_at', dir: 'desc' },
  confirmed: { sort: 'won_at', dir: 'desc' },
})

/** The list-agnostic fallback, for callers that do not know their status yet. */
const FALLBACK_SORT: SortDefault = Object.freeze({
  sort: DEFAULT_CUSTOMER_SEARCH.sort,
  dir: DEFAULT_CUSTOMER_SEARCH.dir,
})

/**
 * Total and defensive, like everything else on the URL path: `status` arrives
 * from a route param, and a value the map does not know must fall back rather
 * than hand back `undefined` for two fields the whole toolbar reads.
 */
export function defaultSortFor(status?: CustomerStatus): SortDefault {
  if (status === undefined) return FALLBACK_SORT
  return DEFAULT_SORT_BY_STATUS[status] ?? FALLBACK_SORT
}

/**
 * Keys this component owns. Anything else in the URL is passed through.
 *
 * `live` and `liveState` are both listed because the state filter is read
 * under either spelling (see `parseCustomerSearch`) and written under one —
 * so a URL carrying the other must not survive as a stale duplicate.
 *
 * `mode` stays listed although the filter itself is gone: owning the key is
 * what scrubs it from bookmarked URLs instead of carrying it around forever
 * as a foreign param.
 */
const OWNED_KEYS: ReadonlySet<string> = new Set<string>([
  'q',
  'mode',
  'agent',
  /*
   * Owned so it is SCRUBBED, never written.
   *
   * `agentNot` is decided by the view, so no navigation ever emits it — but
   * `serializeCustomerSearch` preserves foreign keys, so a hand-typed
   * `?agentNot=…` would otherwise ride along in the address bar forever,
   * looking like state that means something. Same reason `mode` is still here
   * after its filter was removed.
   */
  'agentNot',
  'live',
  'liveState',
  'shift',
  'jobId',
  'scheduleId',
  'sort',
  'dir',
  'page',
  'size',
  ...COLUMN_FILTERS,
])

const LIVE_VALUES: ReadonlySet<string> = new Set<string>(LIVE_STATES)
const SHIFT_VALUES: ReadonlySet<string> = new Set<string>(SHIFT_FILTERS)
/*
 * `won_at` and `active_hit_at` are here because a set that does not list a sort
 * silently REPLACES it with the default.
 *
 * The rail's Won link asks for `sort=won_at`, and this set rejecting it is why
 * that view came up ordered by `created_at` with the toolbar cheerfully reading
 * "Newest first" — the URL said one thing, the request said another, and
 * nothing anywhere reported a disagreement. Both outcome sorts are valid on the
 * wire (`CustomerQuery['sort']`) and indexed, so the only thing missing was
 * this list's permission.
 */
const SORT_VALUES: ReadonlySet<string> = new Set<string>([
  'created_at',
  'updated_at',
  'name',
  'customer_id',
  'won_at',
  'active_hit_at',
])

/** A hand-edited `?page=999999999` must not become an absurd server offset. */
const MAX_PAGE = 100_000

type SearchRecord = Record<string, unknown>

function readString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

/**
 * A URL flag that is only ever true or absent.
 *
 * Never false: `?won=0` and no `won` at all would be two spellings of one
 * filter, and one of them would end up in somebody's bookmark. The API applies
 * the same rule at its own edge, so the two agree by construction.
 */
function readFlag(value: unknown): boolean {
  return value === true || value === 'true' || value === '1'
}

/**
 * A repeatable query param arrives as a string when there is one value and an
 * array when there are several, so both shapes have to be accepted. Unknown
 * members are dropped rather than passed to the API — a hand-edited URL is
 * the most likely source, and the server should not have to defend against it.
 */
function readEnumArray(value: unknown, allowed: ReadonlySet<string>): string[] {
  const raw: unknown[] = Array.isArray(value) ? value : value === undefined ? [] : [value]
  const out: string[] = []
  for (const item of raw) {
    if (typeof item !== 'string') continue
    if (!allowed.has(item)) continue
    if (out.includes(item)) continue
    out.push(item)
  }
  return out
}

function readStringArray(value: unknown): string[] {
  const raw: unknown[] = Array.isArray(value) ? value : value === undefined ? [] : [value]
  const out: string[] = []
  for (const item of raw) {
    if (typeof item !== 'string') continue
    const trimmed = item.trim()
    if (trimmed === '' || out.includes(trimmed)) continue
    out.push(trimmed)
  }
  return out
}

function readNumber(value: unknown): number {
  if (typeof value === 'number') return value
  if (typeof value === 'string' && value.trim() !== '') return Number(value)
  return Number.NaN
}

function readPage(value: unknown): number {
  const n = readNumber(value)
  if (!Number.isFinite(n)) return 1
  return Math.min(MAX_PAGE, Math.max(1, Math.floor(n)))
}

function readSize(value: unknown): PageSize | null {
  const n = readNumber(value)
  if (!Number.isFinite(n)) return null
  return (PAGE_SIZES as readonly number[]).includes(n) ? (n as PageSize) : null
}

/* ------------------------------------------------------------------ *
 * Rows-per-page preference
 *
 * Page *number* is pure view state and belongs only in the URL. Page *size*
 * is a preference about this operator's screen — someone on a 27" monitor
 * wants 100 rows on every list, every day, and should not have to reselect it
 * after following a link that omits the param. So the URL still wins when it
 * carries `size` (that is what makes a pasted link reproduce exactly), and
 * this is only the fallback when it does not.
 * ------------------------------------------------------------------ */

const SIZE_STORAGE_KEY = 'acs.customers.pageSize'

function readSizePreference(): PageSize | null {
  if (typeof localStorage === 'undefined') return null
  try {
    return readSize(localStorage.getItem(SIZE_STORAGE_KEY))
  } catch {
    // Safari private mode / storage disabled.
    return null
  }
}

function writeSizePreference(size: PageSize): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(SIZE_STORAGE_KEY, String(size))
  } catch {
    /* ignore */
  }
}

/**
 * Total, defensive parse. Never throws, always returns a complete object.
 *
 * `status` decides only what an ABSENT `sort` or `dir` means — see
 * `defaultSortFor`. A URL that states either one always wins, whatever list it
 * is pointed at, which is what keeps a pasted link reproducing exactly.
 */
export function parseCustomerSearch(raw: unknown, status?: CustomerStatus): CustomerSearch {
  const rec: SearchRecord = raw !== null && typeof raw === 'object' ? (raw as SearchRecord) : {}
  const sort = readString(rec['sort'])
  const fallback = defaultSortFor(status)

  // Accept both spellings of the state filter. The route schema that hosts
  // this view has historically called it `live`; this component's own type
  // calls it `liveState`. Reading both means neither side can silently drop
  // the operator's filter on a round trip.
  const liveRaw = rec['liveState'] !== undefined ? rec['liveState'] : rec['live']

  const columns = {} as Record<ColumnFilter, string>
  for (const key of COLUMN_FILTERS) columns[key] = readString(rec[key])

  return {
    q: readString(rec['q']),
    agent: readStringArray(rec['agent']),
    liveState: readEnumArray(liveRaw, LIVE_VALUES) as LiveState[],
    shift: readEnumArray(rec['shift'], SHIFT_VALUES) as ShiftFilter[],
    jobId: readString(rec['jobId']),
    scheduleId: readString(rec['scheduleId']),
    /*
     * Read here or the filter does not exist.
     *
     * This function is the type boundary: everything below it is untrusted URL
     * input and everything above it is `CustomerSearch`, so a key it does not
     * read cannot reach `toCustomerQuery` however faithfully the address bar
     * carries it. That is exactly what happened to the rail's Won link — the
     * URL was right, the tab lit up, and the table asked for all 331 approved
     * customers because `won` never made it across this line.
     *
     * Both spellings accepted, because a boolean crosses a URL as either the
     * real thing (TanStack's JSON search serialiser round-trips it) or as text
     * from a hand-typed or bookmarked link.
     */
    won: readFlag(rec['won']),
    activeHit: readFlag(rec['activeHit']),
    sort: SORT_VALUES.has(sort) ? (sort as CustomerSort) : fallback.sort,
    /*
     * Three-way, not two. `'asc'` and `'desc'` are both explicit statements the
     * URL is entitled to make; ANYTHING ELSE — absent, empty, a typo — is the
     * list's own default, which is not always `desc` in principle and must not
     * be hard-coded to it here.
     */
    dir: rec['dir'] === 'asc' ? 'asc' : rec['dir'] === 'desc' ? 'desc' : fallback.dir,
    page: readPage(rec['page']),
    size: readSize(rec['size']) ?? readSizePreference() ?? DEFAULT_PAGE_SIZE,
    ...columns,
  }
}

/**
 * Serialise back to a URL object, omitting anything at its default so the
 * address bar stays readable. Foreign keys (another feature's params) are
 * preserved untouched.
 *
 * Takes the same `status` as the parse, and MUST be given the same one: the two
 * are a pair, and the omission rule below is only sound if both sides agree on
 * what "default" means. Written independently for `sort` and `dir` on purpose —
 * `won_at asc` on the Confirmed list emits `?dir=asc` alone, and reads back as
 * `won_at` (the list default) ascending, which is exactly what was asked for.
 */
function serializeCustomerSearch(
  prev: unknown,
  next: CustomerSearch,
  status?: CustomerStatus
): SearchRecord {
  const fallback = defaultSortFor(status)
  const out: SearchRecord = {}
  if (prev !== null && typeof prev === 'object') {
    for (const [key, value] of Object.entries(prev as SearchRecord)) {
      if (!OWNED_KEYS.has(key)) out[key] = value
    }
  }
  if (next.q !== '') out['q'] = next.q
  if (next.agent.length > 0) out['agent'] = [...next.agent]
  // Written as `live`: the shorter spelling is the one the route schema
  // recognises, and `parseCustomerSearch` reads it back into `liveState`.
  if (next.liveState.length > 0) out['live'] = [...next.liveState]
  if (next.shift.length > 0) out['shift'] = [...next.shift]
  for (const key of COLUMN_FILTERS) {
    if (next[key] !== '') out[key] = next[key]
  }
  if (next.jobId !== '') out['jobId'] = next.jobId
  if (next.scheduleId !== '') out['scheduleId'] = next.scheduleId
  if (next.sort !== fallback.sort) out['sort'] = next.sort
  if (next.dir !== fallback.dir) out['dir'] = next.dir
  if (next.page > 1) out['page'] = next.page
  if (next.size !== DEFAULT_PAGE_SIZE) out['size'] = next.size
  return out
}

/**
 * The router is deliberately addressed through a loose local type.
 *
 * This component has to compile before the route tree that hosts it exists,
 * and it must keep compiling when that route's `validateSearch` schema
 * changes. `parseCustomerSearch` is the real type boundary — everything above
 * it is fully typed, everything below it is treated as untrusted input, which
 * is exactly what a URL is.
 */
type LooseNavigate = (opts: {
  search: (prev: SearchRecord) => SearchRecord
  replace?: boolean
  resetScroll?: boolean
}) => void

/**
 * Parsed, validated filter state for the current URL.
 *
 * Pass the list's `status` wherever it is known. Omitting it is not a bug — the
 * result differs only for a URL that states no `sort`, and only on a list whose
 * default is not the global one — but the two views would then disagree about
 * what an empty address bar means, so every caller that has a status hands it in.
 */
export function useCustomerSearch(status?: CustomerStatus): CustomerSearch {
  const raw = useSearch({ strict: false }) as unknown
  // The router structurally shares its search object, so this recomputes only
  // when a param actually changes — not on every render of the table.
  return React.useMemo(() => parseCustomerSearch(raw, status), [raw, status])
}

/**
 * Anything that changes *which* rows match invalidates the current page
 * number. Being left on "page 7 of 2" after narrowing a filter is the classic
 * pagination bug, and the fix belongs here — in the one function every control
 * goes through — rather than in each control that happens to remember.
 */
function changesResultSet(patch: Partial<CustomerSearch>): boolean {
  for (const key of Object.keys(patch)) {
    if (key !== 'page') return true
  }
  return false
}

/**
 * Patch one or more filters. Uses `replace` so filtering never spams history.
 *
 * `status` is threaded through to BOTH halves of the round trip below, and that
 * pairing is the load-bearing part: read the previous URL with one list's
 * defaults and write it back with another's, and a sort nobody touched gets
 * either stamped into the address bar or silently dropped out of it.
 */
export function useSetCustomerSearch(
  status?: CustomerStatus
): (patch: Partial<CustomerSearch>) => void {
  const navigate = useNavigate() as unknown as LooseNavigate

  return React.useCallback(
    (patch: Partial<CustomerSearch>) => {
      if (patch.size !== undefined) writeSizePreference(patch.size)

      navigate({
        replace: true,
        resetScroll: false,
        search: (prev: SearchRecord) => {
          const next: CustomerSearch = { ...parseCustomerSearch(prev, status), ...patch }
          // An explicit page in the patch always wins; otherwise any filter,
          // sort or size change sends the operator back to the first page.
          if (patch.page === undefined && changesResultSet(patch)) next.page = 1
          return serializeCustomerSearch(prev, next, status)
        },
      })
    },
    [navigate, status]
  )
}

/** True when anything is filtering the list. Drives "Clear all" and the empty state. */
export function hasActiveFilters(search: CustomerSearch): boolean {
  if (
    search.q !== '' ||
    search.agent.length > 0 ||
    search.liveState.length > 0 ||
    search.shift.length > 0 ||
    search.jobId !== '' ||
    search.scheduleId !== ''
  ) {
    return true
  }
  for (const key of COLUMN_FILTERS) {
    if (search[key] !== '') return true
  }
  return false
}

/**
 * Filters only — sort and page size are intentionally preserved by "Clear all".
 * Page is not: a different result set means the old page number means nothing.
 */
export const CLEARED_FILTERS: Partial<CustomerSearch> = Object.freeze({
  q: '',
  agent: [],
  liveState: [],
  shift: [],
  jobId: '',
  scheduleId: '',
  page: 1,
  ...EMPTY_COLUMN_FILTERS,
})

/** How many pages a result set of `total` rows fills at `size` per page. */
export function pageCountOf(total: number | null, size: number): number {
  if (total === null || !Number.isFinite(total) || total <= 0 || size <= 0) return 1
  return Math.max(1, Math.ceil(total / size))
}

/**
 * URL view state -> the wire query the data layer sends.
 *
 * `limit`/`offset` default to the page the URL describes, so the common call
 * is `toCustomerQuery(search, status)`. They stay overridable for callers that
 * need a window of their own (an export, a prefetch of the next page).
 */
export function toCustomerQuery(
  search: CustomerSearch,
  status: CustomerStatus,
  segment?: CustomerSegment,
  limit: number = search.size,
  offset: number = (search.page - 1) * search.size
): CustomerQuery {
  const query: CustomerQuery = {
    status,
    sort: search.sort,
    dir: search.dir,
    limit,
    offset: Math.max(0, offset),
    // The row count is what the footer's "of 312" and the page buttons are
    // built from, so it is never skipped for this view.
    withTotal: true,
  }
  if (search.q !== '') query.q = search.q
  /*
   * WHO this view is about — decided by the route, not by the URL.
   *
   * A segmented view is exactly its own agent, and `search.agent` is ignored
   * because that view hides the agent facet: there is nothing for the operator
   * to have expressed, and honouring a stale `?agent=` from a pasted URL could
   * only empty the list.
   *
   * Every other PENDING view is everyone except the segmented agents, because
   * they are worked in their own queue. Applied unconditionally rather than
   * "only when no agent is selected": the two lists must partition the status
   * whatever else is filtered, or ticking an agent would quietly re-admit rows
   * the view exists to exclude. Approved and Confirmed are untouched — their
   * lists still show everybody.
   *
   * `agentNot` is deliberately not part of `CustomerSearch`: a param the
   * operator could delete would put those customers back in Pending, which is
   * the whole thing this prevents.
   */
  if (segment !== undefined) {
    query.agent = [SEGMENT_AGENT_USERNAME[segment]]
  } else {
    if (status === 'pending') query.agentNot = [...SEGMENTED_AGENT_USERNAMES]
    if (search.agent.length > 0) query.agent = search.agent
  }
  if (search.liveState.length > 0) query.liveState = search.liveState
  if (search.shift.length > 0) query.shift = search.shift
  if (search.jobId !== '') query.jobId = search.jobId
  if (search.scheduleId !== '') query.scheduleId = search.scheduleId
  // Only ever set when true — the wire contract has no "not won" and no "no
  // active hit", for the index reason set out on the type above.
  if (search.won) query.won = true
  if (search.activeHit) query.activeHit = true
  // The column filters carry the same name on the wire as in the URL, which is
  // what lets this be a loop instead of seven lines that can fall out of step.
  for (const key of COLUMN_FILTERS) {
    if (search[key] !== '') query[key] = search[key]
  }
  return query
}

/* ------------------------------------------------------------------ *
 * Agents
 * ------------------------------------------------------------------ */

export interface AgentOption {
  username: string
  name: string | null
  count: number
}

/**
 * The agent roster changes on the order of weeks, so it is cached hard. Every
 * customer view mounts this hook and they all share one entry.
 */
export function useAgentOptions(): UseQueryResult<AgentOption[]> {
  return useQuery({
    queryKey: ['agents'] as const,
    queryFn: ({ signal }) => apiGet<AgentOption[]>('/api/agents', undefined, signal),
    staleTime: 5 * 60_000,
  })
}

/* ------------------------------------------------------------------ *
 * Small pieces (module scope — referentially stable)
 * ------------------------------------------------------------------ */

const SEARCH_DEBOUNCE_MS = 250

/**
 * Every control on this bar is 44px tall on a phone and 36px from `sm` up.
 * 44 is the touch-target floor; 36 is what looks right next to 14.5px type on
 * a pointer device. One constant so they cannot drift apart.
 */
const CONTROL_HEIGHT = 'h-11 sm:h-9'

const STATUS_NOUN: Readonly<Record<CustomerStatus, string>> = Object.freeze({
  pending: 'pending',
  approved: 'approved',
  confirmed: 'confirmed',
})

/** Lower-cased, for the case-insensitive membership test in `agentItems`. */
const SEGMENTED_USERNAME_SET: ReadonlySet<string> = new Set(
  SEGMENTED_AGENT_USERNAMES.map((u) => u.toLowerCase())
)

/**
 * What this LIST calls the customers on it, for the search box's label and the
 * empty state.
 *
 * A segmented queue names itself rather than its status: "No Telegram
 * customers" is the true sentence, and "No pending customers" on a screen the
 * operator opened from a button marked Telegram reads like a bug.
 */
export function listNounFor(status: CustomerStatus, segment?: CustomerSegment): string {
  return segment === undefined ? STATUS_NOUN[status] : SEGMENT_LABEL[segment]
}

interface FilterTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon: React.ReactNode
  label: string
  summary: string | null
  count: number
}

/**
 * Radix's `Trigger asChild` clones this element and hands it a ref plus its own
 * props, so the component has to forward both — a trigger that swallows them
 * renders fine and simply never opens.
 */
const FilterTrigger = React.forwardRef<HTMLButtonElement, FilterTriggerProps>(
  function FilterTrigger({ icon, label, summary, count, className, ...rest }, ref) {
    return (
      <Button
        ref={ref}
        variant="secondary"
        size="sm"
        className={cn(
          CONTROL_HEIGHT,
          'max-w-[210px] rounded-md bg-s1 px-3',
          count > 0 && 'border-accent/40 bg-accent-soft text-accent hover:border-accent/60',
          className
        )}
        aria-label={
          count > 0 ? `${label} filter, ${count} selected` : `Filter by ${label.toLowerCase()}`
        }
        {...rest}
      >
        <span aria-hidden="true" className="opacity-80">
          {icon}
        </span>
        <span className="truncate">{summary ?? label}</span>
        <ChevronDown aria-hidden="true" className="opacity-60" />
      </Button>
    )
  }
)

/**
 * A removable statement of one active filter. Chips exist because a filter you
 * cannot see is a filter you will forget you set, and then misread the table.
 */
function FilterChip(p: { label: string; value: string; onRemove: () => void }) {
  return (
    <span className="inline-flex h-7 items-center gap-1.5 rounded-full border border-line bg-s2 pr-1 pl-2.5 text-[12px] text-fg">
      <span className="text-faint">{p.label}</span>
      <span className="max-w-[190px] truncate font-medium">{p.value}</span>
      <button
        type="button"
        onClick={p.onRemove}
        aria-label={`Remove filter ${p.label} ${p.value}`}
        className="flex size-5 flex-none items-center justify-center rounded-full text-faint transition-colors duration-100 hover:bg-s3 hover:text-fg"
      >
        <X aria-hidden="true" className="size-3" />
      </button>
    </span>
  )
}

/* ---------------- the Shift filter's words and pictures ---------------- */

/** What the menu row says: the row glyph's own label, or `Not set`. */
function shiftFilterLabel(token: ShiftFilter): string {
  return token === 'unset' ? 'Not set' : SHIFT_PRIORITY_LABEL[SHIFT_FILTER_PRIORITY[token]]
}

/** The one-word form the trigger's summary and the chips carry. */
const SHIFT_FILTER_SHORT: Readonly<Record<ShiftFilter, string>> = Object.freeze({
  any: 'Any',
  day: 'Day',
  night: 'Night',
  unset: 'Not set',
})

/**
 * Lucide outlines, not the row's drawn glyphs: a menu row is a 15px icon slot
 * beside a word, and the drawn sun and moon are shapes built for 18px in a
 * line of their own. The outlines wear the same two theme colours, so the
 * menu and the row still agree about which colour means which shift.
 */
function shiftFilterIcon(token: ShiftFilter): React.ReactNode {
  switch (token) {
    case 'day':
      return <Sun aria-hidden="true" className="text-sun" />
    case 'night':
      return <Moon aria-hidden="true" className="text-moon" />
    case 'any':
      return <SunMoon aria-hidden="true" />
    case 'unset':
      return <CircleDashed aria-hidden="true" className="text-faint" />
  }
}

function summarise(values: readonly string[], label: (v: string) => string): string | null {
  if (values.length === 0) return null
  if (values.length <= 2) return values.map(label).join(', ')
  const first = values[0]
  return `${first === undefined ? '' : label(first)} +${values.length - 1}`
}

function toggleValue<T extends string>(list: readonly T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value]
}

const numberFormat = new Intl.NumberFormat('en-US')

/**
 * Sort, as an explicit control rather than only a header click.
 *
 * The table header is the fast path on a laptop, but it cannot be the only
 * one: below `md` there is no header at all (the list renders as cards), and
 * from `md` up only two of the four sort keys have a column of their own. A
 * six-item menu covers every key at every width, and because it writes the
 * same two URL params the headers do, the two can never disagree.
 */
interface SortOption {
  key: string
  label: string
  sort: CustomerSort
  dir: 'asc' | 'desc'
}

const SORT_OPTIONS: readonly SortOption[] = [
  { key: 'created_at:desc', label: 'Newest first', sort: 'created_at', dir: 'desc' },
  { key: 'created_at:asc', label: 'Oldest first', sort: 'created_at', dir: 'asc' },
  { key: 'updated_at:desc', label: 'Recently updated', sort: 'updated_at', dir: 'desc' },
  { key: 'name:asc', label: 'Name A–Z', sort: 'name', dir: 'asc' },
  { key: 'name:desc', label: 'Name Z–A', sort: 'name', dir: 'desc' },
  { key: 'customer_id:asc', label: 'Customer ID', sort: 'customer_id', dir: 'asc' },
]

/**
 * The two OUTCOME sorts, which are offered only where they describe something.
 *
 * `won_at` and `active_hit_at` order a list by a fact most customers do not
 * have: sorting Pending by "when they won" would arrange 196 people by a field
 * none of them carries. So neither is a permanent menu item.
 *
 * They are not permanently hidden either, and that distinction is new. The
 * Confirmed list now OPENS on `won_at desc`, and a default an operator cannot
 * return to is a trap — pick "Name A–Z" once and the only route back to the
 * order the list is meant to have would be editing the URL. So the option
 * appears exactly where it means something: on Confirmed, and on any list
 * already filtered to the matching outcome.
 */
const OUTCOME_SORT_OPTIONS: Readonly<Record<'won_at' | 'active_hit_at', SortOption>> =
  Object.freeze({
    won_at: { key: 'won_at:desc', label: 'Recently won', sort: 'won_at', dir: 'desc' },
    active_hit_at: {
      key: 'active_hit_at:desc',
      label: 'Recently missed',
      sort: 'active_hit_at',
      dir: 'desc',
    },
  })

/**
 * The menu for one view: the six ordinary orders, plus whichever outcome sort
 * this list is about.
 *
 * The outcome option leads rather than trails. On Confirmed it is the order the
 * list is already in, and a checked item at the bottom of six unchecked ones
 * reads like an afterthought instead of like the default it is.
 */
function sortOptionsFor(
  status: CustomerStatus,
  search: Pick<CustomerSearch, 'won' | 'activeHit'>
): readonly SortOption[] {
  const lead: SortOption[] = []
  if (status === 'confirmed' || search.won) lead.push(OUTCOME_SORT_OPTIONS.won_at)
  if (search.activeHit) lead.push(OUTCOME_SORT_OPTIONS.active_hit_at)
  return lead.length === 0 ? SORT_OPTIONS : [...lead, ...SORT_OPTIONS]
}

/**
 * What the trigger reads.
 *
 * Falls back to the outcome labels even on a view whose menu does not offer
 * them, because the URL can still ask for one — the rail's Won link does, on the
 * Approved list. Without that the readout said "Custom", which is the toolbar
 * admitting it does not know what it is showing.
 */
function sortOptionLabel(sort: CustomerSort, dir: 'asc' | 'desc'): string {
  const match = SORT_OPTIONS.find((o) => o.sort === sort && o.dir === dir)
  if (match !== undefined) return match.label
  const outcome = OUTCOME_SORT_OPTIONS[sort as 'won_at' | 'active_hit_at']
  if (outcome === undefined) return 'Custom'
  // Same field, opposite end. "Recently won" is a lie for `won_at asc`.
  return dir === outcome.dir ? outcome.label : outcome.label.replace('Recently', 'Earliest')
}

/* ------------------------------------------------------------------ *
 * Toolbar
 * ------------------------------------------------------------------ */

export interface CustomerToolbarProps {
  status: CustomerStatus
  /**
   * The segmented queue this toolbar belongs to, if any.
   *
   * Changes two things and nothing else: the noun in the search label, and
   * whether the agent facet is offered at all (see `agentItems`).
   */
  segment?: CustomerSegment
  /** `Page.total` — `null` when the server skipped the count. */
  total: number | null
  /** `Page.tookMs` — the server-side query duration. */
  tookMs: number | undefined
  /** Number of rows actually held on the client, for the "showing N of M" case. */
  loaded?: number
  /** Dims the readout while a refetch is in flight. */
  loading?: boolean
  /**
   * Rendered after the Sort control. The table drops its bulk job/shift editor
   * in here — the toolbar itself knows nothing about selection, which is the
   * table's state, so the control arrives fully wired rather than as props to
   * re-plumb.
   */
  bulkSlot?: React.ReactNode
  className?: string
}

export function CustomerToolbar(p: CustomerToolbarProps) {
  const search = useCustomerSearch(p.status)
  const setSearch = useSetCustomerSearch(p.status)
  const agents = useAgentOptions()

  const inputRef = React.useRef<HTMLInputElement | null>(null)
  const timerRef = React.useRef<number | null>(null)
  /** Last value *this* component pushed, so an external URL change is detectable. */
  const pushedRef = React.useRef(search.q)

  const [term, setTerm] = React.useState(search.q)

  // Adopt the URL when it changes from anywhere other than this input
  // (back button, a saved link, the command palette).
  React.useEffect(() => {
    if (search.q === pushedRef.current) return
    pushedRef.current = search.q
    setTerm(search.q)
  }, [search.q])

  React.useEffect(() => {
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current)
    }
  }, [])

  const commitTerm = React.useCallback(
    (value: string) => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current)
        timerRef.current = null
      }
      const next = value.trim()
      pushedRef.current = next
      setSearch({ q: next })
    },
    [setSearch]
  )

  const onTermChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      setTerm(value)
      if (timerRef.current !== null) window.clearTimeout(timerRef.current)
      // 250ms: long enough that a fast typist produces one request, short
      // enough that the table feels like it is reacting to the keystroke.
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null
        pushedRef.current = value.trim()
        setSearch({ q: value.trim() })
      }, SEARCH_DEBOUNCE_MS)
    },
    [setSearch]
  )

  const onTermKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        commitTerm(event.currentTarget.value)
      } else if (event.key === 'Escape' && event.currentTarget.value !== '') {
        event.preventDefault()
        setTerm('')
        commitTerm('')
      }
    },
    [commitTerm]
  )

  const onClearTerm = React.useCallback(() => {
    setTerm('')
    commitTerm('')
  }, [commitTerm])

  // "/" focuses search from anywhere on the page, the way every console the
  // operators already use behaves.
  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return
      const target = event.target as HTMLElement | null
      if (target && target.closest('input, textarea, select, [contenteditable="true"]')) return
      event.preventDefault()
      inputRef.current?.focus()
      inputRef.current?.select()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  /* ---------------- multi-selects ---------------- */

  const agentRows = agents.data
  const agentLabel = React.useCallback(
    (username: string) => {
      const match = agentRows?.find((a) => a.username === username)
      return match?.name ?? username
    },
    [agentRows]
  )

  const agentItems = React.useMemo<DropdownItem[]>(() => {
    if (agents.isPending) {
      return [{ key: '__loading', label: 'Loading agents…', disabled: true }]
    }
    if (agents.isError) {
      return [{ key: '__error', label: 'Could not load agents', disabled: true }]
    }
    /*
     * On PENDING, the segmented agents are not offered.
     *
     * Their customers are in their own queue, so this list excludes them by
     * construction — selecting one here would AND an inclusion with the view's
     * exclusion and guarantee an empty table, under a label showing a non-zero
     * count (the `/api/agents` facet counts every status). A filter whose only
     * possible outcome is nothing is worse than no filter.
     *
     * Only on Pending: Telegram customers genuinely live in Approved and
     * Confirmed, where filtering to them is a real question.
     */
    const rows = (agentRows ?? []).filter(
      (a) => p.status !== 'pending' || !SEGMENTED_USERNAME_SET.has(a.username.toLowerCase())
    )
    if (rows.length === 0) {
      return [{ key: '__empty', label: 'No agents', disabled: true }]
    }
    const items: DropdownItem[] = rows.map((agent) => ({
      key: agent.username,
      label: `${agent.name ?? agent.username} (${agent.count})`,
      checked: search.agent.includes(agent.username),
      onSelect: () => setSearch({ agent: toggleValue(search.agent, agent.username) }),
    }))
    if (search.agent.length > 0) {
      items.push({
        key: '__clear',
        label: 'Clear agent filter',
        separatorBefore: true,
        onSelect: () => setSearch({ agent: [] }),
      })
    }
    return items
  }, [agentRows, agents.isPending, agents.isError, search.agent, setSearch, p.status])

  const liveItems = React.useMemo<DropdownItem[]>(() => {
    const items: DropdownItem[] = LIVE_STATES.map((state) => ({
      key: state,
      label: LIVE_STATE_META[state].label,
      checked: search.liveState.includes(state),
      onSelect: () => setSearch({ liveState: toggleValue(search.liveState, state) }),
    }))
    if (search.liveState.length > 0) {
      items.push({
        key: '__clear',
        label: 'Clear state filter',
        separatorBefore: true,
        onSelect: () => setSearch({ liveState: [] }),
      })
    }
    return items
  }, [search.liveState, setSearch])

  /*
   * Four checkable rows in the domain's own order — Any, Day, Night — with
   * "Not set" last, because it is the odd one out: a state of the record
   * rather than a preference of the person. Multi-select like State, so "Day
   * + Any" (everyone who would take a day shift) is one filter, not two views.
   */
  const shiftItems = React.useMemo<DropdownItem[]>(() => {
    const items: DropdownItem[] = SHIFT_FILTERS.map((token) => ({
      key: token,
      label: shiftFilterLabel(token),
      icon: shiftFilterIcon(token),
      checked: search.shift.includes(token),
      onSelect: () => setSearch({ shift: toggleValue(search.shift, token) }),
    }))
    if (search.shift.length > 0) {
      items.push({
        key: '__clear',
        label: 'Clear shift filter',
        separatorBefore: true,
        onSelect: () => setSearch({ shift: [] }),
      })
    }
    return items
  }, [search.shift, setSearch])

  const sortItems = React.useMemo<DropdownItem[]>(
    () =>
      sortOptionsFor(p.status, search).map((option) => ({
        key: option.key,
        label: option.label,
        checked: search.sort === option.sort && search.dir === option.dir,
        onSelect: () => setSearch({ sort: option.sort, dir: option.dir }),
      })),
    [p.status, search, setSearch]
  )

  /* ---------------- chips ---------------- */

  const chips = React.useMemo(() => {
    const out: { key: string; label: string; value: string; onRemove: () => void }[] = []

    if (search.q !== '') {
      out.push({
        key: 'q',
        label: 'Search',
        value: search.q,
        // Through `commitTerm` so a debounce still in flight is cancelled —
        // otherwise the term the operator just removed reappears 250ms later.
        onRemove: () => {
          setTerm('')
          commitTerm('')
        },
      })
    }
    for (const username of search.agent) {
      out.push({
        key: `agent:${username}`,
        label: 'Agent',
        value: agentLabel(username),
        onRemove: () => setSearch({ agent: search.agent.filter((a) => a !== username) }),
      })
    }
    for (const state of search.liveState) {
      out.push({
        key: `live:${state}`,
        label: 'State',
        value: LIVE_STATE_META[state].label,
        onRemove: () => setSearch({ liveState: search.liveState.filter((s) => s !== state) }),
      })
    }
    for (const token of search.shift) {
      out.push({
        key: `shift:${token}`,
        label: 'Shift',
        value: shiftFilterLabel(token),
        onRemove: () => setSearch({ shift: search.shift.filter((t) => t !== token) }),
      })
    }
    /*
     * The header filter row gets chips too, and that is not decoration.
     *
     * The table drops columns as the window narrows — Email below `lg`, Job and
     * Cities below `xl`, Work hours below `1536` — so a filter typed on a wide
     * screen has no input to show it on a narrow one. Without a chip it would
     * be an invisible reason for a short list, and there would be no way to
     * clear it short of editing the URL.
     */
    for (const key of COLUMN_FILTERS) {
      const value = search[key]
      if (value === '') continue
      out.push({
        key,
        label: COLUMN_FILTER_LABELS[key],
        value,
        onRemove: () => setSearch({ [key]: '' } as Partial<CustomerSearch>),
      })
    }
    if (search.jobId !== '') {
      out.push({
        key: 'jobId',
        label: 'Job',
        value: search.jobId,
        onRemove: () => setSearch({ jobId: '' }),
      })
    }
    if (search.scheduleId !== '') {
      out.push({
        key: 'scheduleId',
        label: 'Schedule',
        value: search.scheduleId,
        onRemove: () => setSearch({ scheduleId: '' }),
      })
    }
    return out
  }, [search, setSearch, agentLabel, commitTerm])

  const onClearAll = React.useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
    pushedRef.current = ''
    setTerm('')
    setSearch(CLEARED_FILTERS)
  }, [setSearch])

  /* ---------------- perf readout ---------------- */

  const rowsLabel =
    p.total === null
      ? p.loaded === undefined
        ? '—'
        : `${numberFormat.format(p.loaded)}+`
      : numberFormat.format(p.total)
  const tookLabel = p.tookMs === undefined ? '—' : `${Math.max(0, Math.round(p.tookMs))}ms`

  return (
    <div
      className={cn(
        'flex flex-none flex-col gap-2.5 border-b border-line bg-s1 px-3 py-3 sm:px-4 sm:py-2.5',
        p.className
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-[288px]">
          <Input
            ref={inputRef}
            value={term}
            onChange={onTermChange}
            onKeyDown={onTermKeyDown}
            onClear={onClearTerm}
            icon={<Search aria-hidden="true" />}
            // 16px on a phone: anything smaller and iOS zooms the whole page
            // the moment the field takes focus, which the operator then has to
            // pinch back out of.
            className={cn(
              CONTROL_HEIGHT,
              'w-full [&_input]:text-[16px] sm:[&_input]:text-[13.5px]'
            )}
            // Deliberately not type="search": the native clear affordance would
            // sit on top of the styled one the Input primitive already renders.
            type="text"
            spellCheck={false}
            autoComplete="off"
            aria-label={`Search ${listNounFor(p.status, p.segment)} customers`}
            placeholder="Search name, email, ID, job…"
          />
          {term === '' ? (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute top-1/2 right-2.5 hidden -translate-y-1/2 sm:block"
            >
              <Kbd>/</Kbd>
            </span>
          ) : null}
        </div>

        {/*
          Hidden on a segmented queue: every row on it has the same agent, so
          the facet's only honest selection is the one the view already applies.
        */}
        {p.segment === undefined ? (
          <Dropdown
            label="Filter by agent"
            items={agentItems}
            trigger={
              <FilterTrigger
                icon={<Users />}
                label="Agent"
                count={search.agent.length}
                summary={
                  search.agent.length > 0
                    ? `Agent: ${summarise(search.agent, agentLabel) ?? ''}`
                    : null
                }
              />
            }
          />
        ) : null}

        <Dropdown
          label="Filter by session state"
          items={liveItems}
          trigger={
            <FilterTrigger
              icon={<Activity />}
              label="State"
              count={search.liveState.length}
              summary={
                search.liveState.length > 0
                  ? `State: ${summarise(search.liveState, (s) => LIVE_STATE_META[s as LiveState].label) ?? ''}`
                  : null
              }
            />
          }
        />

        <Dropdown
          label="Filter by shift priority"
          items={shiftItems}
          trigger={
            <FilterTrigger
              icon={<SunMoon />}
              label="Shift"
              count={search.shift.length}
              summary={
                search.shift.length > 0
                  ? `Shift: ${summarise(search.shift, (t) => SHIFT_FILTER_SHORT[t as ShiftFilter]) ?? ''}`
                  : null
              }
            />
          }
        />

        <Dropdown
          label="Sort customers"
          items={sortItems}
          trigger={
            <FilterTrigger
              icon={<ArrowUpDown />}
              label="Sort"
              count={0}
              summary={`Sort: ${sortOptionLabel(search.sort, search.dir)}`}
              aria-label={`Sort order, currently ${sortOptionLabel(search.sort, search.dir)}`}
            />
          }
        />

        {p.bulkSlot ?? null}

        <div className="ml-auto hidden items-center gap-3 pl-2 sm:flex">
          {/* The product's performance claim, stated plainly and continuously.
              Mono + tabular so the digits do not jitter as they update. */}
          <Tooltip
            side="left"
            content={
              <span>
                Rows matching the current filter, and the time the API spent on the query. Only
                one page of them is fetched at a time.
              </span>
            }
          >
            <span
              className={cn(
                'tabular cursor-default font-mono text-[11.5px] whitespace-nowrap text-faint',
                p.loading && 'opacity-50'
              )}
            >
              {rowsLabel} rows · {tookLabel}
            </span>
          </Tooltip>
        </div>
      </div>

      {chips.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {chips.map((chip) => (
            <FilterChip
              key={chip.key}
              label={chip.label}
              value={chip.value}
              onRemove={chip.onRemove}
            />
          ))}
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearAll}
            className="ml-0.5 h-7"
            aria-label="Clear all filters"
          >
            <X aria-hidden="true" />
            Clear all
          </Button>
        </div>
      ) : null}
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * Pagination
 *
 * Pinned under the table, outside its scroll container: the operator must be
 * able to reach "next page" without first scrolling to the bottom of the page
 * they are already done with.
 * ------------------------------------------------------------------ */

type PageSlot = number | 'gap-left' | 'gap-right'

/**
 * At most seven slots: the first page, the last page, the current page with a
 * neighbour either side, and an ellipsis wherever the run is broken. The
 * window is clamped so the control never changes width as the operator pages
 * through — a row of buttons that reflows under the cursor gets misclicked.
 */
function pageSlots(page: number, pageCount: number): PageSlot[] {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, i) => i + 1)
  }

  const slots: PageSlot[] = [1]
  const start = Math.max(2, Math.min(page - 1, pageCount - 4))
  const end = Math.min(pageCount - 1, Math.max(page + 1, 5))

  if (start > 2) slots.push('gap-left')
  for (let n = start; n <= end; n += 1) slots.push(n)
  if (end < pageCount - 1) slots.push('gap-right')
  slots.push(pageCount)

  return slots
}

const PAGE_BUTTON =
  'tabular relative inline-flex h-9 min-w-9 items-center justify-center rounded-md border px-2 ' +
  'font-mono text-[12.5px] transition-colors duration-100 ' +
  'disabled:cursor-not-allowed disabled:opacity-40'

const PAGE_BUTTON_IDLE = 'border-transparent text-muted hover:bg-s3 hover:text-fg'
/** Indigo is "the thing you have selected", and the current page is exactly that. */
const PAGE_BUTTON_ACTIVE = 'border-accent bg-accent-soft text-accent'

export interface CustomerPaginationProps {
  /**
   * Which list this is paging.
   *
   * Only ever used for the sort default — this control writes `page` and `size`
   * and nothing else. It still has to be told, because every write round-trips
   * the whole search object: without it, pressing Next on Confirmed would read
   * the URL against the wrong default and stamp a redundant `?sort=won_at` into
   * the address bar that the toolbar's own writes then leave back out.
   */
  status: CustomerStatus
  /** 1-based. */
  page: number
  size: PageSize
  /** `Page.total`. `null` when the server could not count exactly — see below. */
  total: number | null
  /** Rows on the page currently rendered, for the "Showing 51–74" tail. */
  rowCount: number
  /** Dims the readout while the next page is in flight. */
  loading?: boolean
  className?: string
}

export function CustomerPagination(p: CustomerPaginationProps) {
  const setSearch = useSetCustomerSearch(p.status)

  /**
   * `total === null` is a real state, not an error: when a live-state filter is
   * active the API refines the page in memory, and if that refinement hits its
   * scan cap it cannot say how many rows match in total. Everything derived
   * from a page *count* is therefore dropped rather than rendered as "of NaN" —
   * Next stays enabled while the page came back full, which is the only honest
   * signal that there is more behind it.
   */
  const exact = p.total !== null
  const pageCount = exact ? pageCountOf(p.total, p.size) : null
  const page = pageCount === null ? p.page : Math.min(p.page, pageCount)

  const first = p.rowCount === 0 ? 0 : (page - 1) * p.size + 1
  const last = p.rowCount === 0 ? 0 : (page - 1) * p.size + p.rowCount

  const hasPrev = page > 1
  const hasNext = pageCount === null ? p.rowCount >= p.size : page < pageCount

  const goto = React.useCallback(
    (next: number) => {
      setSearch({ page: Math.max(1, next) })
    },
    [setSearch]
  )

  const onPrev = React.useCallback(() => goto(page - 1), [goto, page])
  const onNext = React.useCallback(() => goto(page + 1), [goto, page])

  const onSizeChange = React.useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const next = readSize(event.target.value)
      // Page resets to 1 inside `useSetCustomerSearch` — a size change moves
      // every row boundary, so the old page number means nothing.
      if (next !== null) setSearch({ size: next })
    },
    [setSearch]
  )

  const slots = pageCount === null ? [] : pageSlots(page, pageCount)

  return (
    <nav
      aria-label="Pagination"
      className={cn(
        'flex flex-none items-center justify-between gap-2 border-t border-line bg-s1',
        'px-2 py-2 sm:px-4',
        p.className
      )}
    >
      {/* Left: what you are looking at. Mono + tabular so the numbers do not
          shuffle sideways as they change. Hidden on a phone, where the space
          is worth more to the controls. */}
      <p
        className={cn(
          'tabular hidden min-w-0 flex-1 truncate font-mono text-[11.5px] text-faint sm:block',
          p.loading && 'opacity-50'
        )}
      >
        {p.rowCount === 0
          ? 'No rows'
          : exact
            ? `Showing ${numberFormat.format(first)}–${numberFormat.format(last)} of ${numberFormat.format(p.total ?? 0)}`
            : `Showing ${numberFormat.format(first)}–${numberFormat.format(last)}`}
      </p>

      <div className="flex flex-1 items-center justify-center gap-1 sm:flex-none">
        <Button
          variant="ghost"
          size="sm"
          className="h-9 px-2"
          disabled={!hasPrev}
          aria-label="Previous page"
          onClick={onPrev}
        >
          <ChevronLeft aria-hidden="true" />
          <span className="hidden sm:inline">Prev</span>
        </Button>

        {/* Phone: one compact position readout instead of a row of numbers. */}
        <span className="tabular px-1.5 font-mono text-[12.5px] text-muted sm:hidden">
          {pageCount === null ? `Page ${page}` : `${page}/${pageCount}`}
        </span>

        <span className="hidden items-center gap-1 sm:flex">
          {slots.map((slot) =>
            typeof slot === 'number' ? (
              <button
                key={slot}
                type="button"
                onClick={() => goto(slot)}
                aria-label={`Page ${slot}`}
                aria-current={slot === page ? 'page' : undefined}
                className={cn(PAGE_BUTTON, slot === page ? PAGE_BUTTON_ACTIVE : PAGE_BUTTON_IDLE)}
              >
                {slot}
              </button>
            ) : (
              <span
                key={slot}
                aria-hidden="true"
                className="px-0.5 text-[12.5px] text-faint select-none"
              >
                …
              </span>
            )
          )}
        </span>

        <Button
          variant="ghost"
          size="sm"
          className="h-9 px-2"
          disabled={!hasNext}
          aria-label="Next page"
          onClick={onNext}
        >
          <span className="hidden sm:inline">Next</span>
          <ChevronRight aria-hidden="true" />
        </Button>
      </div>

      <div className="flex flex-1 items-center justify-end gap-2">
        <label className="flex items-center gap-2 text-[12.5px] text-faint">
          <span className="hidden md:inline">Rows per page</span>
          <span className="relative flex items-center">
            <select
              value={p.size}
              onChange={onSizeChange}
              aria-label="Rows per page"
              // 16px below `sm` for the same reason as the search box: a
              // smaller font makes iOS zoom the page on focus.
              className={cn(
                'h-11 appearance-none rounded-md border border-line bg-s1 pr-7 pl-2.5 sm:h-9',
                'text-[16px] text-fg sm:text-[13px]',
                'transition-colors duration-100 hover:border-line-strong'
              )}
            >
              {PAGE_SIZES.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <ChevronDown
              aria-hidden="true"
              className="pointer-events-none absolute right-2 size-3.5 text-faint"
            />
          </span>
        </label>
      </div>
    </nav>
  )
}
